import type { CalendlyBookingHealth } from '../../types';
import {
  blockingQuestions,
  formatDurations,
  inviteDurationsSentences,
} from './calendlyDurations';

export interface HealthIssue {
  key: string;
  /** Null for a one-line note. */
  title: string | null;
  body: string;
  /** MED-1365: what to do, on its own line in bold. Empty when another note says it. */
  action: string;
}

function formatQuestions(names: string[]): string {
  return names.map(n => `"${n}"`).join(', ');
}

/**
 * MED-1363: each Calendly setting that stops Calendly from taking participant
 * bookings. Calendly refuses bookings through Medley on a free plan, allows 5
 * a day on a trial, and cannot take a booking for an event type with a
 * required question.
 *
 * MED-1365: participants still book online in each case. Medley then sends
 * the booking as a Google Calendar invite from the Medley calendar. So the
 * notes are information, not warnings. They never tell the coach to upgrade
 * or to pay.
 */
export function healthIssues(
  health: CalendlyBookingHealth,
  audience: 'coach' | 'admin'
): HealthIssue[] {
  const coach = audience === 'coach';
  // MED-1365: the calendar that gets the invites, by the same address that
  // the invite uses (invite_email from the API).
  const email = health.invite_email ?? null;
  const toCalendar = email ? ` to ${email}` : '';
  const invitesSentence = `Medley sends each booking as a Google Calendar invite from the Medley calendar${toCalendar}.`;
  const theCalendar = email
    ? `the calendar for ${email}`
    : 'the calendar that gets these invites';
  const connect = coach
    ? `Connect ${theCalendar} to Calendly, so Calendly does not offer a booked time again.`
    : `Ask the coach to connect ${theCalendar} to Calendly, so Calendly does not offer a booked time again.`;
  const issues: HealthIssue[] = [];
  // MED-1365 QA round 3: the one plan check. On a free plan Calendly takes
  // no booking through Medley, and the free plan note says so. A note that
  // offers a way back to Calendly (a length, a question) would contradict
  // it, so those notes show only when the plan can book through Calendly:
  // paid or trial, or a plan that Calendly did not give.
  const calendlyCanBook = health.plan_stage !== 'free';

  if (health.plan_stage === 'free') {
    issues.push({
      key: 'free',
      title: coach
        ? 'Bookings come as Google Calendar invites'
        : 'Free Calendly plan',
      body: coach
        ? `Free Calendly plan: participants still book online. ${invitesSentence}`
        : `Participants still book online. ${invitesSentence}`,
      action: connect,
    });
  } else if (health.plan_stage === 'trial') {
    const after = `After that, and after the trial ends, participants still book online. Medley then sends each booking as a Google Calendar invite from the Medley calendar${toCalendar}.`;
    issues.push({
      key: 'trial',
      title: coach
        ? 'Calendly trial: some bookings come as calendar invites'
        : 'Calendly trial',
      body: coach
        ? `Your Calendly trial lets Medley book 5 sessions a day through Calendly. ${after}`
        : `The coach's Calendly trial lets Medley book 5 sessions a day through Calendly. ${after}`,
      action: connect,
    });
  }

  // MED-1365 (QA round 2): lengths whose event type is off or not mapped.
  // The plan note already carries the action, so it is not said twice.
  const durationsBody = calendlyCanBook
    ? inviteDurationsSentences(health)
    : null;
  // MED-1365 QA round 9: a trial shows one alert, not two. The trial note
  // ends with the length sentences. A free plan shows no length sentences
  // (QA round 3), so its note does not change.
  const planIssue = health.plan_stage === 'trial' ? issues[0] : undefined;
  if (planIssue && durationsBody) {
    planIssue.body = `${planIssue.body} ${durationsBody}`;
  } else if (durationsBody) {
    issues.push({
      key: 'durations',
      title: 'Some sessions come as Google Calendar invites',
      body: `Participants still book these sessions online. ${durationsBody}`,
      action: issues.length > 0 ? '' : connect,
    });
  }

  // MED-1365 QA round 6: a trial maps only 20 min, so a question on a 45 or
  // 60 min event type does not stop a booking. QA round 9: the dashboard
  // uses the same filter.
  for (const item of blockingQuestions(health)) {
    const many = item.question_names.length > 1;
    const what = `The "${item.event_type_name}" event type (${formatDurations(
      item.durations
    )} sessions) has ${many ? 'required questions' : 'a required question'}: ${formatQuestions(
      item.question_names
    )}. Medley cannot answer ${many ? 'them' : 'it'}, so Calendly cannot take these bookings. Participants still book online: ${invitesSentence}`;
    const these = many ? 'these questions' : 'this question';
    issues.push({
      key: `question-${item.event_type_name}`,
      title: 'A booking question stops Calendly bookings',
      body: coach
        ? `${what} To book through Calendly again, make ${these} optional in Calendly.`
        : `${what} To book through Calendly again, ask the coach to make ${these} optional in Calendly.`,
      // Each booking is a Google invite until then.
      action: connect,
    });
  }

  return issues;
}
